import React from 'react';
import Button from '@material-ui/core/Button';
import Paper from '@material-ui/core/Paper';
import { makeStyles, type Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import cs from 'classnames';
import { useParams, useHistory } from 'react-router-dom';
import useUserInfo from '../auth/useUserInfo';
import { Documents } from '../common/documents';
import type { IDoc } from '../common/documents/types';
import { withLaunchDarkly, type LaunchDarklyFlags } from '../common/launchDarkly';
import NavHeader from '../common/navigation/NavHeader';
import Footer from '../components/common/Footer';
import FooterLinks from '../components/common/FooterLinks';
import { HeaderContext } from '../context/header-context';
import { getUsersGroups } from '../lib/api/account/accountApi';
import { progressPercentageForDocuments } from '../lib/api/common/progressPercentage';
import { chameleonElId } from '../lib/constants';
import { preferencesUrl } from '../lib/environment-config';
import { useIsSmallScreenMediaQuery } from '../lib/isSmallScreen';
import { useDispatch, useShallowSelector } from '../lib/reduxHooks';
import * as templateActions from '../store/applyTemplate/actions';
import * as actions from '../store/envelopeDocuments/actions';
import { errorToast } from '../store/pageFrame/actions';
import { type IDocument, DocumentStatus } from '../store/senderBuild/types';

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 68,
    top: 0,
    overflow: 'auto',
  },
  documents: {
    overflow: 'auto',
    marginBottom: theme.spacing(8),
    padding: `${theme.spacing(3)}px ${theme.spacing(4)}px`,
    width: 'calc(100% - 64px)',
  },
}));

function EnvelopeDocuments({ flags }: { flags: LaunchDarklyFlags }) {
  const store = useShallowSelector((state) => ({
    envelope: state.envelopeDocuments.envelope,
    settings: state.pageFrame.settings,
    settingsFetched: state.pageFrame.settingsFetched,
    documentMapping: state.applyTemplate.documentMapping,
    fetchedAppliedTemplatesForDocuments: state.applyTemplate.fetchedAppliedTemplatesForDocuments,
    updatingTemplateApplication: state.applyTemplate.updatingTemplateApplication,
  }));

  const params = useParams<{ id: string }>();
  const history = useHistory();
  const classes = useStyles();
  const dispatch = useDispatch();
  const headerContext = React.useContext(HeaderContext);
  const userInfo = useUserInfo();
  const areTemplatesEnabled = flags && flags['enable-templates-access'];
  const hideNavItems = flags && flags['hide-build-ui-elements'];
  const showPreferencesMenuItem = flags && flags['add-automatic-time-stamp-signatures-to-user-preferences'];
  const isSmallScreen = useIsSmallScreenMediaQuery();

  const envelopeFetched = !!store.envelope;
  React.useEffect(() => {
    if (store.settingsFetched) {
      headerContext.setContent(
        <NavHeader
          labelName={isSmallScreen ? '' : 'Add documents'}
          breadcrumb={{ name: 'Envelope Management', onClick: () => history.push('/envelopes') }}
          progressBarPercent={25}
          hideNavItems={hideNavItems}
          preferencesUrl={preferencesUrl}
          showPreferences={showPreferencesMenuItem}
          getUsersGroups={getUsersGroups}
          flags={flags}
        />
      );
      if (!envelopeFetched) {
        dispatch(actions.fetchEnvelope(params.id));
      }
    }
    return () => {
      headerContext.setContent(null);
      dispatch(actions.reset());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [store.settingsFetched, isSmallScreen]);

  React.useEffect(() => {
    if (!store.fetchedAppliedTemplatesForDocuments && userInfo?.provisioned) {
      dispatch(templateActions.fetchTemplatesAppliedForDocuments(params.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [store.fetchedAppliedTemplatesForDocuments, userInfo?.provisioned]);

  React.useEffect(() => {
    async function handleFetchEnvelope() {
      await sleep(1000);
      dispatch(actions.fetchEnvelope(params.id));
    }
    const areDocumentsUploading =
      store.envelope && store.envelope.documents.some((doc) => doc.uploadStatus === DocumentStatus.Uploading);
    if (areDocumentsUploading && store.envelope?.documents.length) {
      handleFetchEnvelope();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [store.envelope]);

  if (!envelopeFetched) {
    return null;
  }

  function handleUploadDocument(tempId: string, file: File) {
    dispatch(actions.uploadDocument(tempId, file));
  }

  function handleReorderDocuments(documents: IDocument[]) {
    dispatch(actions.reorderDocuments(documents));
  }

  function handleDeleteDocument(documentId: string) {
    const appliedTemplate = store.documentMapping?.find((d) => d.envelopeDocumentId === documentId);
    if (appliedTemplate) {
      const filteredDocuments = store.documentMapping?.filter((d) => d.envelopeDocumentId != documentId);
      dispatch(templateActions.finalizeBulkTemplates(params.id, filteredDocuments ?? []));
    }
    dispatch(templateActions.updateDocumentMap(documentId, store.documentMapping ?? []));
    dispatch(actions.deleteDocument(documentId));
  }

  function handleUpdateDocuments(documents: IDocument[]) {
    dispatch(actions.updateDocuments(documents));
  }

  function handleError(errStr: string) {
    dispatch(errorToast(errStr));
  }

  return (
    <div className={classes.wrapper}>
      <Typography variant="h3" className={isSmallScreen ? 'mb-10 mt-2 text-center' : 'mb-10 mt-10 text-center'}>
        Upload your documents to get started
      </Typography>

      <div className="max-w-screen-lg mx-auto px-4">
        <Paper
          className={cs('min-h-[500px] relative flex flex-col items-stretch w-full border border-grey-400')}
          style={{ boxShadow: 'none' }}
        >
          <Documents
            documents={store.envelope?.documents as IDoc[]}
            handleUploadDocument={handleUploadDocument}
            // @ts-expect-error -- This code was written prior to enforcing lint rules
            handleUpdateDocuments={handleUpdateDocuments}
            // @ts-expect-error -- This code was written prior to enforcing lint rules
            handleReorderDocuments={handleReorderDocuments}
            handleDeleteDocument={handleDeleteDocument}
            handleError={handleError}
            progressPercentageForDocuments={progressPercentageForDocuments}
            rootClassName={store.envelope?.documents.length === 0 ? 'pt-8' : ''}
            documentsClassName={classes.documents}
            disableDelete={store.updatingTemplateApplication || !store.fetchedAppliedTemplatesForDocuments}
          />
        </Paper>

        <FooterLinks showSocialIcons={false} customMarginTop="0px" className="py-4" />
      </div>

      <Footer>
        {hideNavItems ? null : (
          <Button color="primary" data-spec="exit" onClick={() => history.push('/envelopes')}>
            Exit
          </Button>
        )}
        {areTemplatesEnabled ? (
          <Button
            id={chameleonElId(20)}
            color="primary"
            onClick={() => history.push(`/envelopes/${params.id}/apply-template`)}
            disabled={store.envelope?.documents.length === 0 || store.envelope?.documents.some((d) => d.isUploading)}
          >
            Apply Template
          </Button>
        ) : null}
        <Button
          variant="contained"
          color="primary"
          onClick={() => history.push(`/envelopes/${params.id}/recipients`)}
          className="px-10"
          disabled={
            store.envelope?.documents.length === 0 ||
            store.envelope?.documents.some((d) => d.isUploading) ||
            store.envelope?.documents.some(
              (d) => d.uploadStatus === DocumentStatus.Uploading || d.uploadStatus === DocumentStatus.Failed
            )
          }
          data-spec="next-button"
        >
          Next
        </Button>
      </Footer>
    </div>
  );
}

// eslint-disable-next-line import/no-default-export -- This code was written prior to enforcing lint rules
export default withLaunchDarkly(EnvelopeDocuments);

function sleep(n: number) {
  return new Promise((res) => setTimeout(res, n));
}
